import type { CallId, PortalCall } from '../realtime/contracts';
import { ObservableStore } from './observableStore';
import { preserveUnchanged } from './structuralSharing';

export type CallsSnapshot = {
  readonly calls: readonly PortalCall[];
  readonly callsById: ReadonlyMap<CallId, PortalCall>;
};

const EMPTY: CallsSnapshot = {
  calls: [],
  callsById: new Map(),
};

function byStartTimeDescending(left: PortalCall, right: PortalCall): number {
  return Date.parse(right.startedAtUtc) - Date.parse(left.startedAtUtc);
}

export class CallStore extends ObservableStore<CallsSnapshot> {
  public constructor() {
    super(EMPTY);
  }

  public static get emptySnapshot(): CallsSnapshot {
    return EMPTY;
  }

  public getById(callId: CallId): PortalCall | null {
    return this.getSnapshot().callsById.get(callId) ?? null;
  }

  public upsert(call: PortalCall): void {
    const updated = new Map(this.getSnapshot().callsById);
    updated.set(call.id, this.newerOf(call));

    this.publishCalls(updated);
  }

  public replaceAll(calls: readonly PortalCall[], keep: readonly PortalCall[] = []): void {
    const updated = new Map<CallId, PortalCall>();
    for (const call of [...calls, ...keep]) {
      updated.set(call.id, this.newerOf(call));
    }

    this.publishCalls(updated);
  }

  public drop(callIds: readonly CallId[]): void {
    if (callIds.length === 0) {
      return;
    }

    const updated = new Map(this.getSnapshot().callsById);
    callIds.forEach((callId) => updated.delete(callId));

    this.publishCalls(updated);
  }

  private newerOf(call: PortalCall): PortalCall {
    const known = this.getById(call.id);
    if (known !== null && known.realtimeRevision >= call.realtimeRevision) {
      return known;
    }

    return preserveUnchanged(known ?? undefined, call);
  }

  private publishCalls(callsById: ReadonlyMap<CallId, PortalCall>): void {
    this.publish({
      callsById,
      calls: [...callsById.values()].sort(byStartTimeDescending),
    });
  }
}
